/**
 * Single source of truth for every piece of copy on the site.
 *
 * Previously this file stored raw HTML strings that were injected with
 * `dangerouslySetInnerHTML`. It is now structured data: each page renders it
 * through real components, so the markup is semantic, escaped, and typo-proof.
 */

export const site = {
  name: 'Amirhossein Rahmati',
  initials: 'AR',
  role: 'Principal Software Engineer',
  url: 'https://amirhosseinrahmati.com',
  /* The photograph itself is never rendered — it is the source the ASCII
     portrait is sampled from, and scripts/generate-ascii-portrait.mjs reads it
     off disk. Kept here for social card metadata, which wants an absolute URL. */
  portrait: `${import.meta.env.BASE_URL}media/images/amirhossein-rahmati.jpg`,
};

/**
 * Primary navigation. Order here is the order rendered in the header.
 *
 * /uses is intentionally absent: the page still resolves for anyone with the
 * URL, but it is not advertised anywhere in the UI.
 */
export const navigation = [
  { label: 'About', to: '/' },
  { label: 'Work', to: '/work' },
  { label: 'Contact', to: '/contact' },
];

export const about = {
  title: 'Amirhossein Rahmati',
  eyebrow: 'Principal Software Engineer',
  lead: 'I build user interfaces for the web — the kind that stay fast, accessible, and maintainable long after launch.',
  body: [
    'I specialise in modern JavaScript, with a particular focus on React. Most of my work sits where design systems meet complex application state: component architecture, performance budgets, and the small interaction details that make a product feel considered.',
    'I have shipped production interfaces for teams across the United States, Turkey, New Zealand, Scotland, and Iran — from early prototypes through to long-lived platforms.',
  ],
};

/** Most recent role first. `href: null` renders as plain text, never a dead link. */
export const experience = [
  {
    id: 'ecotrak',
    role: 'Principal Front-End Engineer',
    company: 'Ecotrak LLC',
    location: 'Irvine, California, USA',
    href: 'https://www.ecotrak.com/',
  },
  {
    id: 'optimus',
    role: 'Senior Front-End Web Developer',
    company: 'Optimus Yazılım',
    location: 'Istanbul, Turkey',
    href: 'http://obt.com.tr/',
  },
  {
    id: 'payit',
    role: 'Senior Front-End Web Developer',
    company: 'PayIt',
    location: 'Auckland, New Zealand',
    href: 'https://www.courierit.co.nz/',
  },
  {
    id: 'digivega',
    role: 'Front-End Web Developer',
    company: 'Digivega',
    location: 'Istanbul, Turkey',
    href: 'https://digivega.com/',
  },
  {
    id: 'mealzo',
    role: 'Front-End Web Developer',
    company: 'Mealzo',
    location: 'Glasgow, Scotland',
    href: 'https://mealzo.co.uk/',
    note: 'formerly Weetech',
  },
  {
    id: 'picapixlab',
    role: 'Front-End Web Developer',
    company: 'Picapixlab',
    location: 'Tabriz, Iran',
    href: null,
  },
];

export const contactMethods = [
  {
    id: 'email',
    label: 'Email',
    value: 'mramirhosseinrahmati@gmail.com',
    href: 'mailto:mramirhosseinrahmati@gmail.com',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    value: 'in/amirhosseinrahmati',
    href: 'https://www.linkedin.com/in/amirhosseinrahmati/',
  },
  {
    id: 'github',
    label: 'GitHub',
    value: 'amirhosseinrahmati',
    href: 'https://github.com/amirhosseinrahmati',
  },
];

export const uses = [
  {
    id: 'hardware',
    title: 'Hardware',
    items: ['MacBook'],
  },
  {
    id: 'editor',
    title: 'Editor & Terminal',
    items: ['VS Code — Ariake Dark', 'iTerm2'],
  },
  {
    id: 'design',
    title: 'Design',
    items: ['Figma', 'Adobe Photoshop'],
  },
];

/** Per-route document metadata, keyed by path. */
export const pageMeta = {
  '/': {
    title: 'Amirhossein Rahmati — Front-End Engineer',
    description:
      'Front-end engineer building fast, accessible user interfaces for the web, with a focus on React and design systems.',
  },
  '/work': {
    title: 'Work — Amirhossein Rahmati',
    description:
      'Roles and companies Amirhossein Rahmati has built front-end products for, across the US, Turkey, New Zealand, Scotland, and Iran.',
  },
  '/uses': {
    title: 'Uses — Amirhossein Rahmati',
    description:
      'The hardware, editor, and design tools Amirhossein Rahmati uses day to day.',
  },
  '/contact': {
    title: 'Contact — Amirhossein Rahmati',
    description: 'Get in touch with Amirhossein Rahmati by email, LinkedIn, or GitHub.',
  },
};

export const notFoundMeta = {
  title: 'Page not found — Amirhossein Rahmati',
  description: 'The page you are looking for does not exist.',
};
